import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import omit from 'lodash/omit';
import isEqual from 'lodash/isEqual';
import castArray from 'lodash/castArray';
import last from 'lodash/last';
import noop from 'lodash/noop';
import intersectionWith from 'lodash/intersectionWith';
import Select from '@adsk/react-select';
import List from 'react-virtualized/dist/commonjs/List';
import CellMeasurer, { CellMeasurerCache } from 'react-virtualized/dist/commonjs/CellMeasurer';
import { MDText } from 'i18n-react';

import { withLocale, TSymbolProp } from '@adsk/bim360-matrix-react-helpers/es/locale';
import getOptions from './helpers/getOptions';
import areGroupsEqual from './helpers/areGroupsEqual';
import getDimensions from '@adsk/bim360-matrix-react-helpers/es/getDimensions';
import isNullOrUndefined from '@adsk/bim360-matrix-react-helpers/es/isNullOrUndefined';

import StickyHeaderList from '@adsk/bim360-matrix-react-sticky-header-list';
import Loader from '@adsk/bim360-matrix-react-loader';
import Checkbox from '@adsk/bim360-matrix-react-checkbox';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import Hr from '@adsk/bim360-matrix-react-hr';

import './_SelectDropdown.scss';

import checkmarkIcon from '@adsk/bim360-matrix-react-assets/check-icn.svg';
import cancelInputRound from '@adsk/bim360-matrix-react-assets/cancel-input-round.svg';
import defaultSearchIcon from '@adsk/bim360-matrix-react-assets/magnifying-glass.svg';

// We pass all props down to `react-select` component. Therefor, we need to specify props we don't want to pass
// down to that component in order not to override expected behaviours. Whichever props are specified in
// `Select` or used for internal logic, should be in this list.
const OMIT_PROPS = [
  'selectedOptions',
  'multi',
  'grouped',
  'menuRef',
  'animated',
  'locale',
  'searchPlaceholder',
  'searchIcon',
  'noResultsValue',
  'separatorValue',
  'className',
  'name',
  'value',
  'optionRenderer',
  'valueRenderer',
  'options',
  'menuRenderer',
  'closeOnSelect',
  'clearable',
  'onChange',
  'onOpen',
  'onClose',
  'searchable',
  'placeholder',
  'className',
  'noResultsText',
  'onInputChange',
  'valueKey'
];

const arraysEqual = (arr1, arr2) =>
  arr1.length === arr2.length && arr1.every((item, index) => arr2[index] === item);
const invokeOnChange = cb => {
  let previousArgs = [];
  let previousResult;
  return (...args) => {
    if (arraysEqual(args, previousArgs)) {
      return previousResult;
    } else {
      previousArgs = args;
      previousResult = cb(...args);
      return previousResult;
    }
  };
};

export class SelectDropdown extends Component {
  constructor(props) {
    super(props);

    this.customRowRendererOptions = {};
    this.customKeyMapperOptions = {};
    this.customRenderFooterOptions = {};

    // General handlers
    this.keyMapper = this.keyMapper.bind(this, this.customKeyMapperOptions);
    this.filterOption = this.filterOption.bind(this);
    this.isSelected = this.isSelected.bind(this);
    this.getValue = this.getValue.bind(this);
    this.getStickyHeaderListElementId = this.getStickyHeaderListElementId.bind(this);

    // Event handlers
    this.handleOnMultiChange = this.handleOnMultiChange.bind(this);
    this.handleOnInputChange = invokeOnChange(this.handleOnInputChange.bind(this));
    this.handleOnClose = this.handleOnClose.bind(this);
    this.handleOnOpen = this.handleOnOpen.bind(this);

    // Render functions
    this.renderFooter = this.renderFooter.bind(this, this.customRenderFooterOptions);
    this.renderMenu = this.renderMenu.bind(this);
    this.renderArrow = this.renderArrow.bind(this);
    this.renderNoResultsText = this.renderNoResultsText.bind(this);
    this.originalRenderOptionItem = this.renderOptionItem.bind(this);
    this.renderOptionItem = this.originalRenderOptionItem.bind(this, this.customRowRendererOptions);
    this.renderOption = this.renderOption.bind(this);
    this.renderValue = this.renderValue.bind(this);

    this._optionsCellCache = new CellMeasurerCache({
      defaultHeight: 35,
      keyMapper: this.keyMapper
    });
    this.shouldRerenderAfterFirstOpen = true;

    this.state = {
      /**
       * values that are selected when we first open the dropdown
       */
      selectionValueMap: this.getSelectedOptions(props),
      /**
       * all selected values above and also values that are being selected by the user after the dropdown was opened
       */
      selectionValueMapDuringSelection: {},
      isOpen: false,
      options: this.getOptions(props)
    };

    this.groups = [];
    this.prefixGroup = { items: []};
  }

  keyMapper({ menuProps }, rowIndex) {
    const options = menuProps ? menuProps.options : this.state.options;
    return this.getValue(options[rowIndex].original);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const newState = {};
    if (
      !isEqual(this.props.selectedOptions, nextProps.selectedOptions) ||
      this.props.selectedOptions !== nextProps.selectedOptions
    ) {
      if (this.state.isOpen) {
        newState.selectionValueMapDuringSelection = this.getSelectedOptions(nextProps);
      } else {
        newState.selectionValueMap = this.getSelectedOptions(nextProps);
      }
    }
    if (this.props.options !== nextProps.options) {
      newState.options = this.getOptions(
        nextProps,
        newState.selectionValueMap || this.state.selectionValueMap
      );
    }

    this.setState(newState);
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevState.isOpen && this.state.isOpen) {
      this.props.onOpen();
      if (this.shouldRerenderAfterFirstOpen) {
        this.shouldRerenderAfterFirstOpen = false;
        this.forceUpdate();
      }
    } else if (prevState.isOpen && !this.state.isOpen) {
      this.props.onClose();
    }

    if (this.shouldRebindRenderItem) {
      this.shouldRebindRenderItem = false;
    }
  }

  getSelectedOptions(props = this.props) {
    return castArray(props.selectedOptions).reduce((selectedOptions, item) => {
      if (!item) return selectedOptions;

      selectedOptions[this.getValue(item, props)] = true;
      return selectedOptions;
    }, {});
  }

  getOptions(props = this.props, selectedValues = {}) {
    return getOptions({
      options: props.options,
      prefixOptions: props.singleSelectionPrefixList,
      selectedValues,
      grouped: props.grouped,
      multi: props.multi,
      valueGetter: this.getValue
    });
  }

  getLabel(option) {
    return this.props.labelGetter ? this.props.labelGetter(option) : option[this.props.labelKey];
  }

  getSubtext(option) {
    return this.props.subtextGetter
      ? this.props.subtextGetter(option)
      : option[this.props.subtextKey];
  }

  getValue(option, props = this.props) {
    return option[props.valueKey];
  }

  isSelected(map = this.state.selectionValueMap) {
    return option => map[this.getValue(option.original || option)];
  }

  getOptionData(option) {
    return {
      label: this.getLabel(option),
      value: this.getValue(option),
      subtext: this.getSubtext(option)
    };
  }

  filterOption(option, filter) {
    if (!filter) return true;

    const { label, subtext, value } = this.getOptionData(option.original);
    const lowerCaseFilter = filter.toLowerCase();
    return (
      [label, value, subtext].filter(o => !!o && o.toLowerCase().indexOf(lowerCaseFilter) > -1)
        .length > 0
    );
  }

  onChange = option => {
    if (!option) {
      return;
    }

    this.props.multi ? this.handleOnMultiChange(option) : this.onSingleChange(option);
  };

  onSingleChange = option => {
    const value = this.getValue(option.original);
    if (!this.isSelected()(option)) {
      const selection = { [value]: true };

      this.setState({
        selectionValueMap: selection,
        selectionValueMapDuringSelection: selection,
        options: this.getOptions(this.props, selection)
      });

      this.props.onChange(option.original);
    } else if (this.props.triggerChangeOnReselect) {
      this.props.onChange(option.original);
    }
  };

  handleOnMultiChange(option) {
    const value = this.getValue(option.original);

    const selectionValueMapDuringSelection = {
      ...this.state.selectionValueMapDuringSelection,
      [value]: !this.state.selectionValueMapDuringSelection[value]
    };

    if (this.isSelected(this.state.selectionValueMapDuringSelection)(option)) {
      delete selectionValueMapDuringSelection[value];
    }

    this.setState({ selectionValueMapDuringSelection });

    if (this.props.grouped) {
      this.shouldRebindRenderItem = true;
    }

    this.props.onChange(
      this.state.options
        .filter(this.isSelected(selectionValueMapDuringSelection))
        .map(item => item.original)
    );
  }

  clearSelection = () => {
    this.setState({
      selectionValueMap: {},
      selectionValueMapDuringSelection: {},
      options: this.getOptions()
    });
    this.props.onChange(this.props.multi ? [] : null);
  };

  renderClear = (menuProps, getContainerProps) => {
    const className = classNames('SelectDropdown__clear-button', {
      'SelectDropdown__clear-button--disabled': !menuProps.valueArray.length
    });

    return (
      <div
        {...getContainerProps({ className, onClick: this.clearSelection })}
        data-testid="SelectDropdown__clear-selection"
      >
        {this.props[TSymbolProp].translate('CLEAR')}
      </div>
    );
  };

  renderFooter({ menuProps }, { getContainerProps = props => props } = {}) {
    const { footerComponent, clearable } = this.props;

    return footerComponent ? (
      <div {...getContainerProps()}>{footerComponent}</div>
    ) : clearable ? (
      this.renderClear(menuProps, getContainerProps)
    ) : null;
  }

  shouldRenderSeparator(option, options) {
    const value = this.getValue(option.original);
    const separatorValues = castArray(this.props.separatorValue);

    if (separatorValues.includes(value)) {
      return true;
    }

    return (
      this.props.multi && !this.props.grouped && last(options.filter(this.isSelected())) === option
    );
  }

  _getLabel = (optionRenderer, value, label, subtext) => {
    return optionRenderer ? optionRenderer(value, label, subtext) : label;
  };

  _getSubtext = ({ subtext, optionRenderer, tooltipValue, elementKey }) => {
    if (subtext && !optionRenderer) {
      const subtextClasses = classNames('SelectDropdown__option-subtext', {
        'SelectDropdown__option-subtext--multi': this.props.multi
      });
      return (
        <div key={elementKey} className={subtextClasses} title={tooltipValue}>
          {subtext}
        </div>
      );
    }

    return null;
  };

  _getTooltipValue = (optionRenderer, label, subtext) => {
    return optionRenderer ? null : [label, subtext].filter(_ => _).join('\n');
  };

  _getMultiOptionTemplate = ({ option }) => {
    const { label, subtext, value } = this.getOptionData(option.original);
    const isGroupOptionSelected = this.props.grouped && this.isSelected()(option);
    const isCustomOptionRenderer = !!this.props.optionRenderer;
    const _label = this._getLabel(this.props.optionRenderer, value, label, subtext);
    const tooltipValue = this._getTooltipValue(isCustomOptionRenderer, label, subtext);
    let selectedLabel, _subtextElement;

    if (isGroupOptionSelected) {
      // Showing label + group name if selected
      selectedLabel = `${_label} (${option.customData.group.header})`;
    } else {
      // Subtext only if option is not selected
      _subtextElement = this._getSubtext({
        subtext,
        isCustomOptionRenderer,
        tooltipValue,
        elementKey: 2
      });
    }

    const displayValue = selectedLabel || _label;
    const isSelected = this.isSelected(this.state.selectionValueMapDuringSelection)(option);
    const customValueProps = isSelected
      ? { 'data-testid': `SelectDropdown__selected-option-value__${value}` }
      : {};

    return [
      <div key="1" className="SelectDropdown__multi-option-container" title={tooltipValue}>
        <Checkbox
          className="SelectDropdown__checkbox-icon"
          checked={isSelected}
          focusOnClick={false}
        />
        <div className="SelectDropdown__multi-option" {...customValueProps}>
          {displayValue}
        </div>
      </div>,
      _subtextElement
    ];
  };

  _getSingleOptionTemplate = ({ option }) => {
    const { label: optionLabel, subtext, value } = this.getOptionData(option.original);
    const optionRenderer = this.props.optionRenderer;
    const label = this._getLabel(optionRenderer, value, optionLabel, subtext);
    const tooltipValue = this._getTooltipValue(optionRenderer, optionLabel, subtext);
    const subtextElement = this._getSubtext({
      subtext,
      optionRenderer,
      tooltipValue,
      elementKey: 2
    });
    const isSelected = this.isSelected(this.state.selectionValueMapDuringSelection)(option);
    const customValueProps = isSelected
      ? { 'data-testid': `SelectDropdown__selected-option-value__${value}` }
      : {};

    return [
      <div key="1" title={tooltipValue} className="SelectDropdown__single-option-value-container">
        <span className="SelectDropdown__single-option-value" {...customValueProps}>
          {label}
        </span>
        {isSelected ? (
          <SvgIcon className="SelectDropdown__checkmark-icon">{checkmarkIcon}</SvgIcon>
        ) : null}
      </div>,
      subtextElement
    ];
  };

  renderOption(rendererProps) {
    return this.props.multi
      ? this._getMultiOptionTemplate(rendererProps)
      : this._getSingleOptionTemplate(rendererProps);
  }

  renderValue(option) {
    const {
      multi,
      searchIcon,
      searchable,
      searchPlaceholder,
      valueRenderer,
      options,
      grouped
    } = this.props;
    if (this.state.isOpen && searchable) {
      if (multi) {
        const inputValue = this.selectComponent.getInputValue();
        if (inputValue !== '') {
          return <span>{inputValue}</span>;
        }
      }

      const placeholderClasses = classNames('SelectDropdown__manual-placeholder', {
        'SelectDropdown__manual-placeholder-with-search-icon': searchIcon
      });

      return <span className={placeholderClasses}>{searchPlaceholder}</span>;
    }

    const value = this.getValue(option);
    const selectingValuesArray = Object.keys(
      this.state.isOpen ? this.state.selectionValueMapDuringSelection : this.state.selectionValueMap
    );

    let label = '';
    if (multi && !grouped) {
      if (selectingValuesArray.length === 1) {
        const selectingOption = options.find(
          option => this.getValue(option) === selectingValuesArray[0]
        );
        label = selectingOption ? this.getLabel(selectingOption) : '';
      }
    } else {
      label = this.getLabel(option.original);
    }

    const defaultValueTemplate =
      selectingValuesArray.length === 1 ? (
        <span data-testid="SelectDropdown__input-display-value">{label}</span>
      ) : (
        <span data-testid="SelectDropdown__input-display-value">
          {this.props[TSymbolProp].translate('ITEMS_SELECTED', {
            amount: selectingValuesArray.length
          })}
        </span>
      );

    if (multi) {
      return valueRenderer ? valueRenderer(selectingValuesArray) : defaultValueTemplate;
    }

    return valueRenderer ? valueRenderer(value, label) : defaultValueTemplate;
  }

  renderNoResultsText() {
    return this.props.isLoadingOptions ? (
      <Loader className="SelectDropdown__options-loader" />
    ) : (
      this.props.noResultsValue || this.props[TSymbolProp].translate('NO_RESULTS_FOUND')
    );
  }

  handleMouseDownOnArrow = event => {
    // prevent default event handlers
    event.stopPropagation();
    event.preventDefault();
    // Set the select component input value to empty
    this.selectComponent.setInputValue('');
  };

  renderArrow({ onMouseDown, isOpen }) {
    // if we are in a middle of a search - render clear search icon
    if (isOpen && this.selectComponent.getInputValue() !== '') {
      return (
        <span
          className="Select-arrow hide-arrow"
          data-testid="SelectDropdown__clear-search"
          onMouseDown={this.handleMouseDownOnArrow}
        >
          <SvgIcon className="SelectDropdown__clear-search-icon">{cancelInputRound}</SvgIcon>
        </span>
      );
    } else return <span className="Select-arrow" onMouseDown={onMouseDown} />;
  }

  handleOnInputChange(...args) {
    if (this.state.isOpen) {
      this.setState({
        selectionValueMap: this.state.selectionValueMapDuringSelection,
        options: this.getOptions(this.props, this.state.selectionValueMapDuringSelection)
      });
    }

    return this.props.onInputChange(...args);
  }

  handleOnOpen() {
    this.setState({ isOpen: true, selectionValueMapDuringSelection: this.state.selectionValueMap });
  }

  handleOnClose() {
    this.setState({
      isOpen: false,
      selectionValueMap: this.state.selectionValueMapDuringSelection,
      selectionValueMapDuringSelection: {},
      options: this.getOptions(this.props, this.state.selectionValueMapDuringSelection)
    });
  }

  renderOptionItem({ menuProps, outerRenderer, optionGetter }, { index, style, ...rest }) {
    const {
      focusedOption,
      instancePrefix,
      onFocus,
      optionClassName,
      optionComponent,
      options,
      onOptionRef,
      selectValue
    } = menuProps;

    const option = optionGetter({ ...rest, index, options });
    const Option = optionComponent;
    const isFocused = option.original === focusedOption.original;
    const isSelected = this.isSelected(this.state.selectionValueMapDuringSelection)(option);

    const optionClass = classNames(optionClassName, 'Select-option', {
      'is-selected': isSelected && !this.props.multi,
      'is-focused': isFocused,
      'is-disabled': option.original.disabled,
      'is-first-prefix':
        this.props.singleSelectionPrefixList.length &&
        this.getValue(this.props.singleSelectionPrefixList[0]) === this.getValue(option.original) &&
        !isNullOrUndefined(this.getValue(option.original))
    });

    const props = {
      className: optionClass,
      instancePrefix,
      isDisabled: option.original.isDisabled,
      isFocused,
      isSelected,
      onFocus,
      onSelect: o => selectValue(o),
      option,
      index,
      ref: _ => onOptionRef(_, isFocused)
    };

    return outerRenderer({
      ...rest,
      index,
      children: (
        <div
          data-testid={`SelectDropdown__option-value__${this.getValue(option.original)}`}
          style={style}
        >
          <Option {...props} title={option.original.title}>
            {this.renderOption({ ...rest, option, index })}
          </Option>
          {this.shouldRenderSeparator(option, options) ? <Hr /> : null}
        </div>
      )
    });
  }

  getStickyHeaderListElementId({ item, group }) {
    return item ? this.getValue(item.original) : group.id;
  }

  renderGroupMenu(menuProps) {
    const prefixItems = this.props.multi
      ? menuProps.options.filter(this.isSelected())
      : menuProps.options.filter(
        item => this.props.singleSelectionPrefixList.indexOf(item.original) > -1
      );

    const prefixGroup = {
      previousItemCount: 0,
      items: prefixItems,
      id: `__SelectDropdown__prefix__group__id__`
    };

    this.customRowRendererOptions.outerRenderer = ({ children }) => children;
    this.customRowRendererOptions.optionGetter = ({ item }) => item;

    const groups = Object.values(
      menuProps.options.reduce((groups, item) => {
        if (prefixItems.indexOf(item) === -1) {
          const initialGroup = {
            header: item.customData.group.header,
            id: item.customData.group.id,
            items: [],
            getHeaderProps: ({ group }) => ({
              'data-testid': `SelectDropdown__group-header-${group.id}`
            })
          };

          groups[item.customData.group.id] = groups[item.customData.group.id] || initialGroup;
          groups[item.customData.group.id].items = groups[item.customData.group.id].items.concat([
            item
          ]);
        }
        return groups;
      }, {})
    );

    if (
      groups.length !== this.groups.length ||
      intersectionWith(this.groups, groups, areGroupsEqual(this.getValue)).length !== groups.length
    ) {
      this.groups = groups;
    }
    if (!areGroupsEqual(this.getValue)(prefixGroup, this.prefixGroup)) {
      this.prefixGroup = prefixGroup;
    }

    return (
      <StickyHeaderList
        ref={this.props.menuRef}
        className="SelectDropdown__sticky-header-list"
        prefixGroup={this.prefixGroup}
        getId={this.getStickyHeaderListElementId}
        itemRenderer={this.renderOptionItem}
        groups={this.groups}
        renderFooter={this.renderFooter}
        maxWidth={this.props.dropdownMaxWidth}
        maxHeight={this.props.dropdownMaxHeight}
      />
    );
  }

  renderDefaultMenu(menuProps) {
    this.customRowRendererOptions.outerRenderer = ({ index: rowIndex, ...props }) => (
      <CellMeasurer {...props} rowIndex={rowIndex} columnIndex={0} cache={this._optionsCellCache} />
    );
    this.customRowRendererOptions.optionGetter = ({ options, index }) => options[index];
    this.customKeyMapperOptions.menuProps = menuProps;

    const dimensions = getDimensions({
      items: menuProps.options,
      getItemDimensions: index => ({
        height: this._optionsCellCache.getHeight(index),
        width: this._optionsCellCache.getWidth(index)
      }),
      maxHeight: this.props.dropdownMaxHeight,
      maxWidth: this.props.dropdownMaxWidth
    });

    return (
      <div ref={this.props.menuRef}>
        <div>
          <List
            height={dimensions.height}
            rowCount={menuProps.options.length}
            deferredMeasurementCache={this._optionsCellCache}
            rowHeight={(...args) => this._optionsCellCache.rowHeight(...args)}
            rowRenderer={this.renderOptionItem}
            overscanRowCount={20}
            className="SelectDropdown__virtual-list"
            width={dimensions.width}
          />
        </div>
        {this.renderFooter()}
      </div>
    );
  }

  renderMenu(menuProps) {
    if (
      (this.customRowRendererOptions.menuProps &&
        menuProps.focusedOption !== this.customRowRendererOptions.menuProps.focusedOption) ||
      this.shouldRebindRenderItem
    ) {
      this.renderOptionItem = this.originalRenderOptionItem.bind(
        this,
        this.customRowRendererOptions
      );
    }

    this.customRowRendererOptions.menuProps = menuProps;
    this.customRenderFooterOptions.menuProps = menuProps;

    return this.props.grouped ? this.renderGroupMenu(menuProps) : this.renderDefaultMenu(menuProps);
  }

  promptTextCreator = label => this.props[TSymbolProp].translate('CTEATE_OPTION', { label });

  setSelectRef = r => {
    this.selectComponent = r;
  };

  render() {
    const {
      multi,
      searchable,
      valid,
      inputName,
      className,
      clearable,
      grouped,
      animated,
      searchIcon,
      autoSize,
      creatable,
      placeholder = this.props[TSymbolProp].translate('SELECT_PLACEHOLDER'),
      searchPlaceholder = this.props[TSymbolProp].translate('SEARCH_PLACEHOLDER')
    } = this.props;
    const value = Object.keys(
      this.state.isOpen ? this.state.selectionValueMapDuringSelection : this.state.selectionValueMap
    )[0];
    const showSearchIcon = searchIcon && searchable && this.state.isOpen;
    const selectDropdownClasses = classNames(
      'SelectDropdown',
      {
        'SelectDropdown--clearable': clearable,
        'SelectDropdown--grouped': grouped,
        'SelectDropdown--animated': !multi && animated,
        'SelectDropdown--with-input-icon': showSearchIcon,
        'SelectDropdown--invalid': !valid,
        'SelectDropdown--fixed-size': !autoSize
      },
      className
    );

    const SelectComponent = creatable ? Select.Creatable : Select;

    return (
      <div className={selectDropdownClasses}>
        {showSearchIcon ? <div className="SelectDropdown__inputIcon">{searchIcon}</div> : null}
        <SelectComponent
          ref={this.setSelectRef}
          name={inputName}
          value={value}
          filterOption={this.filterOption}
          valueRenderer={this.renderValue}
          options={this.state.options}
          menuRenderer={this.renderMenu}
          arrowRenderer={multi && this.props.searchable ? this.renderArrow : undefined}
          closeOnSelect={!multi}
          clearable={false}
          onChange={this.onChange}
          onOpen={this.handleOnOpen}
          onClose={this.handleOnClose}
          onInputChange={this.handleOnInputChange}
          inputProps={{ 'data-testid': 'SelectDropdown__input-value' }}
          searchable={!!searchable || !!creatable}
          onSelectResetsInput={!multi}
          clearFilterOnSelect={!multi}
          placeholder={this.state.isOpen && !!searchable ? searchPlaceholder : placeholder}
          className={className}
          noResultsText={this.renderNoResultsText()}
          externalFooterHeight={this.props.clearable || this.props.footerComponent ? 30 : undefined}
          menuBuffer={this.props.menuBuffer}
          excludeOutsideScrollbar={true}
          scrollOnOptionSelect={false}
          promptTextCreator={this.promptTextCreator}
          menuStyle={{ overflow: 'hidden' }}
          valueKey="value"
          {...omit(this.props, OMIT_PROPS)}
        />
      </div>
    );
  }
}

SelectDropdown.propTypes = {
  ...Select.propTypes,
  /**
   * List of options to display
   * grouped mode - options should be passed as follows: [ ['group1', [{},{},{}]], ['group2', [{},{},{}]] ]
   */
  options: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object, PropTypes.array])),
  /**
   * Multi mode - List of selected Options
   * Single mode - The selected option (if passed in array like multi mode, the first option will be used)
   */
  selectedOptions: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.object]),
  /**
   * Option's label key name ('title' for example)
   */
  labelKey: PropTypes.string,
  /**
   * Custom label getter. If provider - labelKey is ignored.
   * The handler is of the shape of "(option) => *"
   */
  labelGetter: PropTypes.func,
  /**
   * Option's value key name ('eventKey' for example)
   */
  valueKey: PropTypes.string,
  /**
   * Subtext's value key name ('subtext' for example)
   */
  subtextKey: PropTypes.string,
  /**
   * Custom subtext getter. If provider - subtextKey is ignored.
   * The handler is of the shape of "(option) => *"
   */
  subtextGetter: PropTypes.func,
  /**
   * Whether the dropdown is in multi / single mode
   */
  multi: PropTypes.bool,
  /**
   * Whether the multi dropdown options are grouped
   */
  grouped: PropTypes.bool,
  /**
   * Renders a button to clear selected options
   */
  clearable: PropTypes.bool,
  /**
   * Enable options search
   */
  searchable: PropTypes.bool,
  /**
   * Whether the dropdown menu opens / closes with animation (SelectDropdown width is auto)
   */
  animated: PropTypes.bool,
  /**
   * Whether the dropdown can be opened
   */
  disabled: PropTypes.bool,
  /**
   * Flag indicating whether or not the input is in a valid or invalid state.
   */
  valid: PropTypes.bool,
  /**
   * The locale to use for translated strings
   */
  locale: PropTypes.string,
  /**
   * A callback function for a menu item selection event
   */
  onChange: PropTypes.func,
  /**
   * A callback function for dropdown focus
   */
  onFocus: PropTypes.func,
  /**
   * Function that renders a customized option for single dropdown, (value, label, subtext) => { should return a valid JSX }
   */
  optionRenderer: PropTypes.func,
  /**
   * Function that renders a customized selected value
   * single mode - (value: any, label: any) => { should return a valid JSX }
   * multi mode - (selectedItemValues: any[]) => { should return a valid JSX }
   */
  valueRenderer: PropTypes.func,
  /**
   * Input's name for form handling
   */
  inputName: PropTypes.string,
  /**
   * Placeholder for unselected dropdown
   */
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * Placeholder for searchable dropdown (displayed when dropdown is open)
   */
  searchPlaceholder: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * An icon (react element) to be placed before the search placeholder
   */
  searchIcon: PropTypes.node,
  /**
   * Accepts string or component to display when search results are null
   */
  noResultsValue: PropTypes.node,
  /**
   * An option's value for which a separator is rendered underneath, separating the passed options
   * This could be a custom value or an array of values
   */
  separatorValue: PropTypes.any,
  /**
   * A custom className for the container element of the component
   */
  className: PropTypes.string,
  /**
   * Callback that's called when the dropdown opens.
   */
  onOpen: PropTypes.func,
  /**
   * Callback that's called when the dropdown closes.
   */
  onClose: PropTypes.func,
  /**
   * A custom footer component that will be rendered at the bottom of the dropdown's option.
   * Please note that this prop overrides the clearable prop
   */
  footerComponent: PropTypes.node,
  /**
   * A margin to be placed at the bottom of the dropdown menu. This is used in order to prevent the dropdown from sticking
   * to its container when it overflows its container height
   */
  menuBuffer: PropTypes.number,
  /**
   * options to put first before all the others.
   */
  singleSelectionPrefixList: PropTypes.arrayOf(PropTypes.any),
  /**
   * This function is a callback to handle the ref of what the menuRenderer renders
   */
  menuRef: PropTypes.func,
  /**
   * If enabled, the input will expand as the length of its value increases
   */
  autoSize: PropTypes.bool,
  /**
   * Display a loader while loading options
   */
  isLoadingOptions: PropTypes.bool,
  /**
   * If enabled, you can create a new option if there is no matched result.
   */
  creatable: PropTypes.bool,
  /**
   * Specifies if should trigger onChange when selecting an already selected option
   */
  triggerChangeOnReselect: PropTypes.bool,
  /**
   * The max width (in pixels) that the dropdown can be in, if less it'll behave normally - if more it'll trim the content to match that width.
   */
  dropdownMaxWidth: PropTypes.number,
  /**
   * The max height (in pixels) that the dropdown can be in, if less it'll behave normally - if more it'll trim the content to match that height.
   */
  dropdownMaxHeight: PropTypes.number,
  /**
   * Which attribute to use when filtering the results.
   */
  matchProp: PropTypes.oneOf(['label', 'value', 'any']),
  /**
   * Callback that's called whenever the input changes (available only when searchable={true})
   */
  onInputChange: PropTypes.func
};

SelectDropdown.defaultProps = {
  labelKey: 'label',
  valueKey: 'value',
  subtextKey: 'subtext',
  valid: true,
  onChange: noop,
  onOpen: noop,
  onClose: noop,
  menuBuffer: 10,
  matchProp: 'label',
  singleSelectionPrefixList: [],
  menuRef: noop,
  autoSize: false,
  onInputChange: noop,
  dropdownMaxHeight: 320,
  searchIcon: <SvgIcon className="SelectDropdown__default-search-icon">{defaultSearchIcon}</SvgIcon>
};

export default withLocale(
  SelectDropdown,
  ({ locale }) => new MDText(require(`./i18n/${locale}.json`))
);
